import * as React from "react";

import { useDatasources } from "@/shared/state/visualization/hooks";

import {
  FileSelector,
  FileSelectorSkeleton,
  SelectableFile,
} from "../FileSelector";
import { Topics } from "../Topics";
import { useEphemeralWorkspaceState } from "../WorkspaceCtx/hooks";

import styles from "./FileAndTopicList.module.css";
import { Header } from "./Header";

export function FileAndTopicList() {
  const { firstFileId, isLoadingFiles, estimatedFileCount, selectableFiles } =
    useSelectFiles();

  const [selectedFileId, setSelectedFileId] = React.useState<
    string | undefined
  >(() => {
    if (estimatedFileCount === 1) {
      return firstFileId;
    }
    return undefined;
  });

  const onFileSelected = React.useCallback((file: SelectableFile) => {
    setSelectedFileId(file.fileId);
  }, []);

  const clearFileSelection = React.useCallback(() => {
    setSelectedFileId(undefined);
  }, []);

  const fileSelectorStyles = [
    styles.fileSelector,
    {
      [styles.hidden]: selectedFileId !== undefined,
    },
  ];

  return (
    <>
      {isLoadingFiles ? (
        <FileSelectorSkeleton
          className={fileSelectorStyles}
          estimatedFileCount={estimatedFileCount}
        />
      ) : (
        <FileSelector
          className={fileSelectorStyles}
          files={selectableFiles}
          onFileSelected={onFileSelected}
        />
      )}
      <Topics
        fileId={selectedFileId}
        className={[
          styles.topics,
          {
            [styles.hidden]: selectedFileId === undefined,
          },
        ]}
        header={
          estimatedFileCount > 1 ? (
            <Header className={styles.header} onBack={clearFileSelection} />
          ) : undefined
        }
      />
    </>
  );
}

interface SelectFilesState {
  firstFileId: string | undefined;
  isLoadingFiles: boolean;
  estimatedFileCount: number;
  selectableFiles: SelectableFile[];
}

function useSelectFiles(): SelectFilesState {
  // The current set of fileIds
  const datasources = useDatasources();

  // The resolved fileRecords containing relative path
  const { files: fileRecords, isDataLoading } = useEphemeralWorkspaceState();

  const selectableFiles = React.useMemo(() => {
    return fileRecords.map((file) => {
      return {
        fileId: file.file_id,
        relativePath: file.relative_path,
      };
    });
  }, [fileRecords]);

  // Calculate estimated file count from datasources if available
  const estimatedFileCount = React.useMemo(() => {
    if (selectableFiles.length > 0) {
      return selectableFiles.length;
    }

    // Count file datasources as a true estimate
    const fileDatasources = datasources.filter((ds) => ds.type === "file");

    // If there are no file datasources, assume a dataset with many files
    return fileDatasources.length > 0 ? fileDatasources.length : 10;
  }, [selectableFiles.length, datasources]);

  return {
    firstFileId: datasources.find((datasource) => datasource.type === "file")
      ?.value.fileId,
    isLoadingFiles: isDataLoading,
    estimatedFileCount,
    selectableFiles,
  };
}
